// CONTRACT:P1-CANONICAL-ENVELOPE.1.0
/** Pure, dependency-free ALTITUDE MODEL for the 2.5D-DEPTH milestone.
 *
 *  WHAT: maps (domain, type) -> a plausible altitude in metres above the WGS84 ellipsoid
 *  (HAE), with deterministic per-id jitter so a given aircraft type's flight-level band has
 *  texture instead of rendering as a flat sheet. Surface domains (maritime / ground) return
 *  `null` — they sit on the deck and get NO Z.
 *
 *  WHY: our synthetic scenarios (hormuz, theaters) and the haystack author heading + speed but
 *  NO altitude, so the inspector op-grid showed "—" for every synthetic air track and the map
 *  had nothing to lift in 3D. This gives air/UAS tracks a real Z.
 *
 *  CONVENTION (single source of truth for altitude units): altitude is metres HAE. The scenario
 *  `toEnvelope` projections store it BOTH as `properties.altitude_m` (the canonical store the
 *  inspector + renderer read — same key the live ADS-B path uses, since OpenSky `geo_altitude`
 *  is also HAE) AND as the 3rd GeoJSON coordinate `[lon, lat, altitude_m]` (RFC 7946-legal).
 *  Display layers convert metres -> FL/ft; the map lifts the glyph to `altitude_m` when tilted.
 *
 *  PURE: imports nothing. Deterministic. Safe to import from any plane (scenarios, capabilities)
 *  without coupling to host internals / PDP. */

const M_PER_FT = 0.3048;
/** Flight level (hundreds of feet) -> metres HAE. fl(350) === 35,000 ft === ~10,668 m. */
const fl = (level: number): number => Math.round(level * 100 * M_PER_FT);

/** Deterministic [0,1) hash (FNV-1a) — same shape the scenarios use for coherent jitter. */
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 100000) / 100000;
}

/** A type's altitude band: a centre altitude (m HAE) and a +/- jitter span (m). */
interface Band {
  base: number;
  jitter: number;
}

/** Resolve a band from the domain + free-text type. Order matters: most specific first.
 *  Returns null for surface domains (no Z). */
function bandFor(domain: string | undefined, type: string | undefined): Band | null {
  const d = (domain ?? "").toLowerCase();
  if (d === "maritime" || d === "sea" || d === "surface") return null; // on the water
  if (d === "ground" || d === "land") return null; // on the deck (terrain handled later)

  const air = d === "air" || d === "uas" || d === "uav";
  if (!air) return null; // sensors / unknown-domain entities stay surface for now

  const t = (type ?? "").toLowerCase();

  // hypersonic glide vehicle / boost-glide — near-space cruise band (very high). Most specific
  // first so a HGV does not fall through to the generic air band.
  if (/hyperson|hgv|boost.?glide|glide.?vehicle|waverider/.test(t)) return { base: fl(800), jitter: fl(60) };
  // rotorcraft / rotary ISR — low and slow
  if (/rotor|rotary|helicopter|helo/.test(t)) return { base: 900, jitter: 500 };
  // unmanned — MALE band, mid altitude
  if (/uav|uas|drone|unmanned|rpa/.test(t) || d === "uas" || d === "uav")
    return { base: fl(180), jitter: fl(60) };
  // high-altitude ISR / surveillance orbit (fixed-wing)
  if (/isr|orbit|surveillance|recon|aew|awacs|sentinel/.test(t)) return { base: fl(410), jitter: fl(40) };
  // air-refuelling tanker
  if (/tanker|refuel|kc-?\d/.test(t)) return { base: fl(300), jitter: fl(30) };
  // maritime patrol aircraft (P-8 et al.) — transit/patrol band
  if (/patrol|p-?8|maritime patrol|mpa/.test(t)) return { base: fl(220), jitter: fl(90) };
  // business / private jet — high cruise
  if (/business|bizjet|private jet|gulfstream|learjet/.test(t)) return { base: fl(430), jitter: fl(20) };
  // regional turboprop — lower cruise
  if (/turboprop|regional|prop/.test(t)) return { base: fl(240), jitter: fl(40) };
  // commercial airliner — main cruise band
  if (/airliner|widebody|narrowbody|commercial|civil|airbus|boeing|a3\d\d|b7\d\d/.test(t))
    return { base: fl(370), jitter: fl(40) };
  // tactical air — fighter / strike / EW
  if (/fighter|strike|carrier|threat|ew|electronic|f-?\/?a?-?1?8|combat|attack/.test(t))
    return { base: fl(290), jitter: fl(80) };
  // unidentified / unknown air contact — plausible mid-low (could be anything)
  if (/unident|unknown|contact|track/.test(t)) return { base: fl(150), jitter: fl(80) };

  // default air
  return { base: fl(210), jitter: fl(60) };
}

/** Altitude in metres HAE for an air/UAS entity, or `null` for surface entities.
 *  Deterministic in `id` so re-publishing the same track is idempotent. */
export function altitudeHaeM(
  domain: string | undefined,
  type: string | undefined,
  id: string,
): number | null {
  const band = bandFor(domain, type);
  if (!band) return null;
  const j = (hash01(id + "|alt") - 0.5) * 2 * band.jitter;
  return Math.max(60, Math.round(band.base + j)); // floor at 60 m so nothing clips the deck
}
